/* eslint-disable import/prefer-default-export */
import { LocalizedString } from '@commercetools/platform-sdk';

import { AlgoliaCategory, SynthesizedAlgoliaCategory } from '@/api/algolia';
import { Ancestor, AssetData, CategoryWithCount } from '@/graphql/categoryQuery';

export interface BuiltCategory {
  ancestors: Ancestor[];
  assets?: AssetData[];
  childCount: number;
  description: string;
  hidden: boolean;
  id: string;
  isB2b?: boolean;
  key?: string;
  name: string;
  orderHint?: number;
  path: string;
  sidebarBottom?: string[];
  sidebarTop?: string[];
  stagedProductCount: number;
  topDescription?: string;
  urlName: string;
  version: number;
  wholesaleDescription?: string;
}

interface Options {
  cleanHtml?: boolean;
  locale?: string;
}

const isLocalizedString = (value: unknown, locale = 'en'): value is LocalizedString =>
  Object.prototype.hasOwnProperty.call(value, locale);

const pivotCategoryAttributes = async (
  custom: CategoryWithCount['custom'],
  { cleanHtml = true, locale = 'en' }: Options = {},
) => {
  const { HTMLCleaner } = await import('@/utils/html_cleaner');

  const parsedCustomFields: Pick<
    BuiltCategory,
    | 'hidden'
    | 'isB2b'
    | 'sidebarBottom'
    | 'sidebarTop'
    | 'topDescription'
    | 'urlName'
    | 'wholesaleDescription'
  > = {
    hidden: false,
    isB2b: undefined,
    sidebarBottom: undefined,
    sidebarTop: undefined,
    topDescription: undefined,
    urlName: '',
    wholesaleDescription: undefined,
  };

  const customFieldsRaw = custom?.customFieldsRaw;
  if (!customFieldsRaw) return parsedCustomFields;

  customFieldsRaw.forEach(({ name, value }) => {
    const attribute = name as keyof typeof parsedCustomFields;

    if (typeof value === 'boolean' && (attribute === 'hidden' || attribute === 'isB2b')) {
      parsedCustomFields[attribute] = value;
    } else if (
      Array.isArray(value) &&
      value.length &&
      (attribute === 'sidebarTop' || attribute === 'sidebarBottom')
    ) {
      const parsedValue = value.map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        return item[locale];
      });

      parsedCustomFields[attribute] = parsedValue;
    } else if (typeof value === 'string' || isLocalizedString(value, locale)) {
      let stringValue = typeof value === 'string' ? value : value[locale];

      if (attribute === 'topDescription' && !stringValue.includes('[noparse]')) {
        stringValue = `[noparse]${stringValue}[/noparse]`;
      }
      if (attribute === 'topDescription' || attribute === 'wholesaleDescription') {
        parsedCustomFields[attribute] = cleanHtml ? HTMLCleaner.clean(stringValue) : '';
      } else if (attribute === 'urlName') {
        parsedCustomFields[attribute] = stringValue;
      }
    }
  });

  return parsedCustomFields;
};

export const BuiltCategory = {
  async fromCT(
    rawCategory: CategoryWithCount,
    { cleanHtml = true, locale = 'en' }: Options = {},
  ): Promise<BuiltCategory> {
    const { HTMLCleaner } = await import('@/utils/html_cleaner');

    const {
      ancestors,
      assets,
      childCount,
      custom,
      description: descriptionRaw,
      id,
      key,
      name,
      orderHint,
      stagedProductCount,
      version,
    } = rawCategory;

    const categoryAttributes = await pivotCategoryAttributes(custom, { cleanHtml, locale });

    const builtCategory: BuiltCategory = {
      ...categoryAttributes,
      ancestors,
      assets,
      childCount,
      description: cleanHtml ? HTMLCleaner.clean(descriptionRaw) : '',
      id,
      key,
      name,
      orderHint,
      path: '',
      stagedProductCount,
      version,
    };

    const path = ancestors
      .map((ancestor) => ancestor.custom?.customFieldsRaw?.find((f) => f.name === 'urlName')?.value)
      .join('/');
    builtCategory.path = `${path}/${builtCategory.urlName}/`;

    return builtCategory;
  },
};

export interface CategoryCard extends Omit<AlgoliaCategory, 'image_url' | 'type'> {
  image: string;
  total: number;
  type: AlgoliaCategory['type'] | SynthesizedAlgoliaCategory['type'];
}
